import { useState, useEffect } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import UnderlineExtension from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { marked } from 'marked'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { DatePickerCell } from '@/components/common/DatePickerCell'
import { previewCommitmentLetter } from '@/api/commitmentLetters'
import { useCommitmentLetterExport } from '@/hooks/useCommitmentLetterExport'
import { toast } from '@/stores/useToastStore'
import type { PlanDetailItem } from '@/types'

function isMarkdown(text: string): boolean {
  return /(\*\*|__|##|^\s*[-*]\s)/m.test(text)
}

function toHtml(content: string): string {
  if (!content) return ''
  if (isMarkdown(content)) {
    return marked.parse(content, { async: false }) as string
  }
  return content
}

interface CommitmentLetterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId?: string
  selectedItems: PlanDetailItem[]
  onClearSelection: () => void
}

export function CommitmentLetterModal({
  open,
  onOpenChange,
  planId,
  versionId,
  selectedItems,
  onClearSelection,
}: CommitmentLetterModalProps) {
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [dueDate, setDueDate] = useState<Date | null>(null)

  const {
    isProcessing,
    isCompleted,
    isError,
    progress,
    exportCommitmentLetter,
    reset,
  } = useCommitmentLetterExport(planId, versionId ?? null)

  const previewEditor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      UnderlineExtension,
      TextStyle,
    ],
    content: '',
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-0 focus:outline-none',
      },
    },
  })

  // Fetch preview when modal opens and due date changes
  useEffect(() => {
    if (!open || !versionId || !dueDate) {
      setPreviewContent(null)
      previewEditor?.commands.setContent('')
      return
    }

    setIsLoadingPreview(true)
    setPreviewContent(null)

    const formattedDate = format(dueDate, 'yyyy-MM-dd')

    previewCommitmentLetter(planId, versionId, formattedDate)
      .then(response => {
        setPreviewContent(response.content)
        if (previewEditor) {
          previewEditor.commands.setContent(toHtml(response.content))
        }
      })
      .catch(error => {
        const message =
          error instanceof Error ? error.message : 'Failed to load preview'
        toast.error(message)
      })
      .finally(() => {
        setIsLoadingPreview(false)
      })
  }, [open, planId, versionId, dueDate, previewEditor])

  // On export success: close modal and clear selection
  useEffect(() => {
    if (isCompleted) {
      onOpenChange(false)
      onClearSelection()
    }
  }, [isCompleted, onOpenChange, onClearSelection])

  const handleDownload = () => {
    if (!dueDate) return
    exportCommitmentLetter(
      selectedItems.map(item => item.id),
      format(dueDate, 'yyyy-MM-dd')
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing while processing
    if (isProcessing && !newOpen) return

    if (!newOpen) {
      setPreviewContent(null)
      setDueDate(null)
      reset()
      previewEditor?.commands.setContent('')
    }
    onOpenChange(newOpen)
  }

  const statusText = isProcessing
    ? 'Processing...'
    : isError
      ? 'Export failed'
      : ''

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Create Commitment Letter
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Due Date</label>
          <DatePickerCell value={dueDate} onChange={setDueDate} />
        </div>

        {isProcessing || isError ? (
          <div className="flex flex-col gap-3 py-4">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground text-center">
              {statusText}
            </p>
          </div>
        ) : isLoadingPreview ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : previewContent ? (
          <>
            <div className="flex-1 overflow-y-auto border border-[#d6dae1] rounded-md bg-white p-6">
              <EditorContent editor={previewEditor} />
            </div>

            <div className="flex items-center justify-end">
              <Button
                onClick={handleDownload}
                disabled={!dueDate}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download .ZIP
              </Button>
            </div>
          </>
        ) : !dueDate ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">
              Select a due date to preview the commitment letter
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">
              No preview available
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
